// src/components/Layout.js

import React from 'react';
import {
  AppBar,
  Toolbar,
  Typography,
  IconButton,
  Drawer,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  CssBaseline,
  useMediaQuery,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Button,
  Box,
} from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import AddIcon from '@mui/icons-material/Add';
import GridViewIcon from '@mui/icons-material/GridView';
import ListAltIcon from '@mui/icons-material/ListAlt';
import CelebrationIcon from '@mui/icons-material/Celebration';
import ReceiptIcon from '@mui/icons-material/Receipt';
import PersonIcon from '@mui/icons-material/Person';
import LogoutIcon from '@mui/icons-material/Logout';
import SmartphoneIcon from '@mui/icons-material/Smartphone';
import CloseIcon from '@mui/icons-material/Close';
import { useTheme } from '@mui/material/styles';
import { Link, Outlet, useNavigate, useLocation } from 'react-router-dom';
import { signOut } from 'firebase/auth';
import { auth } from '../firebase';
import api from '../api';
import dayjs from 'dayjs';
import { safeLocalStorage } from '../utils/safeStorage';

const navIcons = {
  Dashboard: <GridViewIcon />,
  Lists: <ListAltIcon />,
  Events: <CelebrationIcon />,
  Purchases: <ReceiptIcon />,
  Profile: <PersonIcon />,
  Logout: <LogoutIcon />,
};

function Layout() {
  const [drawerOpen, setDrawerOpen] = React.useState(false);
  const [openListDialog, setOpenListDialog] = React.useState(false);
  const [openEventDialog, setOpenEventDialog] = React.useState(false);
  const [newListName, setNewListName] = React.useState('');
  const [newEventName, setNewEventName] = React.useState('');
  const [newEventDate, setNewEventDate] = React.useState(dayjs().format('YYYY-MM-DD'));
  const [installBannerVisible, setInstallBannerVisible] = React.useState(false);
  const [isIOS, setIsIOS] = React.useState(false);
  const deferredPrompt = React.useRef(null);

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const navigate = useNavigate();
  const location = useLocation();

  React.useEffect(() => {
    // Migrate old permanent-dismiss flag ('1') to expired — show banner again
    const dismissed = safeLocalStorage.getItem('listlink_install_dismissed');
    if (dismissed === '1') {
      safeLocalStorage.removeItem('listlink_install_dismissed');
    }
    const THIRTY_DAYS = 30 * 24 * 60 * 60 * 1000;
    const fresh = safeLocalStorage.getItem('listlink_install_dismissed');
    if (fresh && Date.now() - Number(fresh) < THIRTY_DAYS) return;

    const handler = (e) => {
      e.preventDefault();
      deferredPrompt.current = e;
      setInstallBannerVisible(true);
    };
    window.addEventListener('beforeinstallprompt', handler);
    return () => window.removeEventListener('beforeinstallprompt', handler);
  }, []);

  React.useEffect(() => {
    const dismissed = safeLocalStorage.getItem('listlink_install_dismissed');
    if (dismissed === '1') {
      safeLocalStorage.removeItem('listlink_install_dismissed');
    }
    const THIRTY_DAYS = 30 * 24 * 60 * 60 * 1000;
    const fresh = safeLocalStorage.getItem('listlink_install_dismissed');
    if (fresh && Date.now() - Number(fresh) < THIRTY_DAYS) return;
    const ios = /(iPhone|iPad|iPod)/i.test(navigator.userAgent);
    const standalone = window.navigator.standalone;
    if (ios && !standalone) {
      setIsIOS(true);
      setInstallBannerVisible(true);
    }
  }, []);

  const handleInstallClick = async () => {
    if (deferredPrompt.current) {
      deferredPrompt.current.prompt();
      await deferredPrompt.current.userChoice;
      deferredPrompt.current = null;
    }
    setInstallBannerVisible(false);
    safeLocalStorage.setItem('listlink_install_dismissed', String(Date.now()));
  };

  const handleInstallDismiss = () => {
    setInstallBannerVisible(false);
    safeLocalStorage.setItem('listlink_install_dismissed', String(Date.now()));
  };

  const drawerWidth = 180;

  const toggleDrawer = (open) => () => {
    setDrawerOpen(open);
  };

  const handleLogout = () => {
    signOut(auth).catch((error) => {
      console.error('Error during logout:', error);
    });
  };

  const handleCreateList = async () => {
    try {
      const response = await api.post('/lists', { name: newListName });
      const newListId = response.data._id;
      setNewListName('');
      setOpenListDialog(false);
      navigate(`/lists/${newListId}`);
    } catch (error) {
      console.error('Error creating list:', error);
    }
  };

  const handleCreateEvent = async () => {
    try {
      await api.post('/events', { name: newEventName, date: newEventDate });
      setNewEventName('');
      setNewEventDate(dayjs().format('YYYY-MM-DD'));
      setOpenEventDialog(false);
      navigate('/events');
    } catch (error) {
      console.error('Error creating event:', error);
    }
  };

  const menuItems = [
    { text: 'Dashboard', path: '/dashboard' },
    {
      text: 'Lists',
      path: '/lists',
      hasAction: true,
      onAction: (e) => {
        e.preventDefault();
        e.stopPropagation();
        setOpenListDialog(true);
      }
    },
    {
      text: 'Events',
      path: '/events',
      hasAction: true,
      onAction: (e) => {
        e.preventDefault();
        e.stopPropagation();
        setOpenEventDialog(true);
      }
    },
    {
      text: 'Purchases',
      path: '/purchases',
      hasAction: true,
      onAction: (e) => {
        e.preventDefault();
        e.stopPropagation();
        navigate('/purchases', { state: { openDialog: true } });
      }
    },
    { text: 'Profile', path: '/profile' },
    { text: 'Logout', action: handleLogout },
  ];

  const isSelected = (path) => {
    if (!path) return false;
    return location.pathname === path || location.pathname.startsWith(path + '/');
  };

  return (
    <Box sx={{ display: 'flex' }}>
      <CssBaseline />
      <AppBar
        position="fixed"
        sx={{
          zIndex: theme.zIndex.drawer + 1,
          background: 'rgba(253,250,245,0.88)',
          backdropFilter: 'blur(12px) saturate(180%)',
          borderBottom: '1px solid',
          borderColor: 'divider',
        }}
      >
        <Toolbar>
          {isMobile && (
            <IconButton
              edge="start"
              onClick={toggleDrawer(true)}
              sx={{ mr: 1, color: '#1A1410' }}
            >
              <MenuIcon />
            </IconButton>
          )}
          <Typography
            variant="h6"
            noWrap
            component={Link}
            to="/dashboard"
            sx={{ textDecoration: 'none', display: 'flex', alignItems: 'baseline' }}
          >
            <Box
              component="span"
              sx={{
                fontFamily: '"Plus Jakarta Sans", sans-serif',
                fontWeight: 800,
                color: '#1A1410',
              }}
            >
              List
            </Box>
            <Box
              component="span"
              sx={{
                fontFamily: '"Fraunces", Georgia, serif',
                fontStyle: 'italic',
                color: 'primary.main',
                fontWeight: 600,
              }}
            >
              Link
            </Box>
          </Typography>
        </Toolbar>
      </AppBar>

      {/* Drawer */}
      <Drawer
        variant={isMobile ? 'temporary' : 'persistent'}
        open={isMobile ? drawerOpen : true}
        onClose={toggleDrawer(false)}
        anchor="left"
        sx={{
          width: drawerWidth,
          flexShrink: 0,
          '& .MuiDrawer-paper': {
            width: drawerWidth,
            boxSizing: 'border-box',
          },
        }}
      >
        <Toolbar />
        <List onClick={isMobile ? toggleDrawer(false) : null} sx={{ pt: 1 }}>
          {menuItems.map((item) => (
            <ListItemButton
              component={item.path ? Link : 'div'}
              to={item.path}
              key={item.text}
              onClick={item.action}
              selected={isSelected(item.path)}
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                pr: item.hasAction ? 1 : 2,
                mb: 0.5,
              }}
            >
              <ListItemIcon sx={{ minWidth: 36, color: isSelected(item.path) ? 'primary.main' : 'text.secondary' }}>
                {navIcons[item.text]}
              </ListItemIcon>
              <ListItemText primary={item.text} />
              {item.hasAction && (
                <IconButton
                  size="small"
                  onClick={item.onAction}
                  sx={{
                    ml: 'auto',
                    width: 28,
                    height: 28,
                    border: '1.5px solid',
                    borderColor: 'primary.main',
                    color: 'primary.main',
                    '&:hover': {
                      backgroundColor: 'rgba(232,137,26,0.08)',
                    },
                  }}
                >
                  <AddIcon sx={{ fontSize: 16 }} />
                </IconButton>
              )}
            </ListItemButton>
          ))}
        </List>
      </Drawer>

      {/* Main Content */}
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          mt: '64px',
          p: { xs: 2, sm: 3 },
          minHeight: 'calc(100vh - 64px)',
        }}
      >
        {installBannerVisible && (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              mb: 2,
              px: 1.5,
              py: 1,
              bgcolor: 'rgba(232,137,26,0.08)',
              borderLeft: '3px solid #E8891A',
              borderRadius: 2,
            }}
          >
            <SmartphoneIcon sx={{ color: '#E8891A', flexShrink: 0, fontSize: 20 }} />
            <Typography variant="body2" sx={{ flexGrow: 1, color: 'text.primary' }}>
              {isIOS
                ? 'Install ListLink: tap Share then "Add to Home Screen" for quick access.'
                : 'Install ListLink for quick access from your home screen.'}
            </Typography>
            {!isIOS && (
              <Button
                size="small"
                variant="contained"
                onClick={handleInstallClick}
                sx={{
                  flexShrink: 0,
                  bgcolor: '#E8891A',
                  '&:hover': { bgcolor: '#D4780F' },
                  textTransform: 'none',
                  fontSize: '0.75rem',
                  py: 0.5,
                  px: 1.5,
                }}
              >
                Install
              </Button>
            )}
            <IconButton size="small" onClick={handleInstallDismiss} sx={{ flexShrink: 0 }}>
              <CloseIcon sx={{ fontSize: 16 }} />
            </IconButton>
          </Box>
        )}
        <Outlet />
      </Box>

      {/* Create List Dialog */}
      <Dialog open={openListDialog} onClose={() => setOpenListDialog(false)}>
        <DialogTitle>Create New List</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="List Name"
            type="text"
            fullWidth
            value={newListName}
            onChange={(e) => setNewListName(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                handleCreateList();
              }
            }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenListDialog(false)}>Cancel</Button>
          <Button onClick={handleCreateList} variant="contained">Create</Button>
        </DialogActions>
      </Dialog>

      {/* Create Event Dialog */}
      <Dialog open={openEventDialog} onClose={() => setOpenEventDialog(false)}>
        <DialogTitle>Create New Event</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 1 }}>
            <TextField
              autoFocus
              margin="dense"
              label="Event Name"
              type="text"
              fullWidth
              value={newEventName}
              onChange={(e) => setNewEventName(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  handleCreateEvent();
                }
              }}
            />
            <TextField
              margin="dense"
              label="Event Date"
              type="date"
              fullWidth
              value={newEventDate}
              onChange={(e) => setNewEventDate(e.target.value)}
              InputLabelProps={{ shrink: true }}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenEventDialog(false)}>Cancel</Button>
          <Button onClick={handleCreateEvent} variant="contained">Create</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default Layout;
